import { type BadgeColor, CircularProgress, Select, StatusBadge } from '@civilgrid/ui/components';
import { Link } from '@tanstack/react-router';
import type { CellContext } from '@tanstack/react-table';
import { DateTime, type Duration } from 'luxon';
import { useMemo } from 'react';
import ReactDatePicker from 'react-datepicker';
import { useAsyncFn } from 'react-use';
import { useSnapshot } from 'valtio';
import { editProject as apiEditProject, updateIntelligenceSummary as apiUpdateSummary } from '~/api/projects2';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { planStatusLabels } from '~/common/projectUtils';
import { rowBadgeLabel } from '~/common/rowBadgeLabel';
import {
  type Agency,
  ConflictRec,
  type IntelligenceSummary,
  type OrganizationDetail,
  type PlanStatus,
  type TLoadedState,
} from '~/common/types';
import { AUTO_COL_WIDTH, type ExportableColumnDef } from '~/components2/Table';
import FreeFormTextCell from '~/components2/Table/Cells/FreeFormTextCell';
import store from '~/store';
import {
  Cell,
  EmptyCell,
  getSummaryValue,
  hasPriorComputeData,
  isMissingDates,
  MissingInfo,
  renderHeaderWithTooltip,
  StaleCell,
  useSummaryConfig,
} from './common';

export const toMiles = (num: number): number => {
  return Number((num / 5280).toFixed(2));
};

const CellLoading = () => {
  return (
    <div className="flex w-full items-center justify-center">
      <CircularProgress indeterminate />
    </div>
  );
};

const CellError = () => {
  return <div className="text-xs italic text-red-500">An error occurred</div>;
};

function renderInfoTextCell({
  text,
  summary,
  testId,
}: {
  text: string;
  summary: IntelligenceSummary;
  testId?: string;
}) {
  if (summary.computeStatus === 'error') {
    if (!hasPriorComputeData(summary)) {
      return <EmptyCell />;
    }
    return (
      <StaleCell
        value={
          <span className="whitespace-pre-wrap" data-testid={testId}>
            {text || '—'}
          </span>
        }
      />
    );
  }

  return (
    <span className="whitespace-pre-wrap" data-testid={testId}>
      {text}
    </span>
  );
}

const recommendationLabels: { [rec in ConflictRec]: string } = {
  [ConflictRec.Accelerate]: 'Accelerate',
  [ConflictRec.Defer]: 'Defer',
  [ConflictRec.NeedsReview]: 'Needs Review',
  [ConflictRec.NoScheduleConflict]: 'No Schedule Conflict',
};

function hasWarning(agencies: Agency[]) {
  return !!agencies?.find((a) => a.type === 'Paving Projects' || a.type === 'Moratoriums' || a.type === 'CIP');
}

export function getConflictArea(summary: IntelligenceSummary): number {
  return Math.round(summary.conflictArea ?? 0);
}

export function getJurisdictionCount(summary: IntelligenceSummary): number {
  return Math.round(
    (summary.incorporatedPlacesCount ?? 0) +
      (summary.countyCount ?? 0) +
      (summary.landUseTransportationDistrictBoundariesCount ?? 0),
  );
}

const useUpdateDate = ({
  projectId,
  summaryId,
  duration,
  currentStart,
  cell,
  field,
}: {
  projectId: string;
  summaryId: string;
  duration?: Duration | undefined;
  currentStart?: DateTime | undefined;
  cell: CellContext<IntelligenceSummary, unknown>;
  field: 'year' | 'startDate' | 'endDate';
}) => {
  const query = useAsyncFn(
    async (value: DateTime | undefined) => {
      let constructionStartDate: DateTime | undefined;
      let constructionEndDate: DateTime | undefined;

      if (field === 'endDate') {
        constructionStartDate = currentStart;
        constructionEndDate = value;
      } else {
        constructionStartDate = value;
        constructionEndDate = constructionStartDate?.plus(duration || { months: 6 });
      }

      const res = await apiEditProject(projectId, {
        attributes: {
          constructionStartDate,
          constructionEndDate,
        },
      });

      if (!res.id) throw new Error('Bad response!');

      cell.table.options.meta?.updateValue?.(projectId, [
        {
          columnId: 'constructionStartDate',
          level: 'project',
          value: constructionStartDate,
        },
        {
          columnId: 'constructionYear',
          level: 'project',
          value: constructionStartDate?.year,
        },
        {
          columnId: 'constructionEndDate',
          level: 'project',
          value: constructionEndDate,
        },
        {
          columnId: 'computeStatus',
          level: 'summary',
          value: 'processing',
        },
      ]);

      cell.table.options.meta?.addToPoll?.(summaryId);

      capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryConstructionDateChanged, {
        project_id: projectId,
        summary_id: summaryId,
        field,
        new_value: value?.toISO() ?? null,
      });
    },
    [projectId, summaryId, duration, currentStart, cell, field],
  );

  return query;
};

export const columns: ExportableColumnDef<IntelligenceSummary>[] = [
  {
    id: 'projectId',
    size: 100,
    header: 'Project ID',
    accessorFn: (s) => s.project.projectId,
    cell: (cell) => {
      const id = cell.row.original.project.id;
      const summaryId = cell.row.original.id;
      const value = cell.getValue<string>();

      const onClick = () => {
        capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryProjectOpened, {
          project_id: id,
          summary_id: summaryId,
        });
      };

      return (
        <Link
          to="/projects/$projectId/$tabId"
          params={{ projectId: id, tabId: 'intelligence' }}
          className="underline"
          data-testid={`cell-project-id-${id}`}
          onClick={onClick}
        >
          {value}
        </Link>
      );
    },
  },
  {
    id: 'name',
    size: AUTO_COL_WIDTH,
    header: 'Name',
    accessorFn: (s) => s.project.name,
    cell: (cell) => {
      const id = cell.row.original.project.id;
      const value = cell.getValue<string>();
      return <span data-testid={`cell-name-${id}`}>{value}</span>;
    },
  },
  {
    id: 'planStatus',
    header: 'Plan Status',
    accessorFn: (s) => planStatusLabels[s.project.planStatus] || '',
    cell: (cell) => {
      const { currentOrganizationDetail } = useSnapshot(store) as typeof store;

      const conflictsSummaryConfig = (currentOrganizationDetail as TLoadedState & OrganizationDetail)
        .conflictsSummaryConfig;
      const config = useSummaryConfig({ conflictsSummaryConfig });

      const projectId = cell.row.original.project.id;
      const current = cell.row.original.project.planStatus;

      const planStatusItems = useMemo(() => {
        return Object.keys(config.statusLabels).map((status) => ({
          value: status,
          label: config.statusLabels[status as PlanStatus],
        }));
      }, [config.statusLabels]);

      const summaryId = cell.row.original.id;

      const [update, fetchUpdate] = useAsyncFn(async (value: PlanStatus) => {
        const res = await apiEditProject(projectId, {
          attributes: {
            planStatus: value,
          },
        });

        if (!res.id) throw new Error('Bad response!');
        cell.table.options.meta?.updateValue?.(projectId, [
          {
            columnId: 'planStatus',
            level: 'project',
            value,
          },
        ]);

        capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryPlanStatusChanged, {
          project_id: projectId,
          summary_id: summaryId,
          from_status: current,
          to_status: value,
        });
      });

      if (update.loading) return <CellLoading />;
      if (update.error) return <CellError />;

      return (
        <div>
          <Select
            aria-label="Plan status"
            options={planStatusItems}
            value={current}
            onValueChange={(v) => v && fetchUpdate(v as PlanStatus)}
            data-testid={`cell-plan-status-${projectId}`}
          />
        </div>
      );
    },
  },
  {
    id: 'constructionYear',
    header: 'Construction Year',
    size: 100,
    accessorFn: (s) => s.project.constructionYear,
    filterFn: 'inNumberRange',
    cell: (cell) => {
      const summary = cell.row.original;
      const projectId = summary.project.id;
      const currentStart = summary.project.constructionStartDate;
      const currentEnd = summary.project.constructionEndDate;
      let duration: Duration | undefined;

      if (currentStart && currentEnd) {
        duration = currentEnd.diff(currentStart);
      }

      const [update, fetchUpdate] = useUpdateDate({
        projectId,
        summaryId: summary.id,
        cell,
        duration,
        field: 'year',
      });

      const d: DateTime | null = summary.project.constructionStartDate || null;
      const v = cell.getValue<number | undefined>();

      if (update.loading) return <CellLoading />;
      if (update.error) return <CellError />;

      return (
        <div
          className="flex h-full w-full items-start justify-start"
          data-testid={`cell-construction-year-${projectId}`}
        >
          <ReactDatePicker
            selected={d?.toJSDate() || null}
            onChange={(d: Date | null) => fetchUpdate(d ? DateTime.fromJSDate(d) : undefined)}
            closeOnScroll
            dateFormat={'yyyy'}
            showYearPicker
            popperClassName="z-10!"
            customInput={
              <div
                className="h-full w-full grow cursor-pointer"
                data-testid={`cell-construction-year-input-${projectId}`}
              >
                {v || <span className="opacity-50">Select Date</span>}
              </div>
            }
          />
        </div>
      );
    },
  },
  {
    id: 'constructionStartDate',
    header: 'Construction Start Date',
    accessorFn: (s) => s.project.constructionStartDate?.toLocaleString() || '',
    cell: (cell) => {
      const summary = cell.row.original;
      const projectId = summary.project.id;
      const currentStart = summary.project.constructionStartDate;
      const currentEnd = summary.project.constructionEndDate;
      let duration: Duration | undefined;

      if (currentStart && currentEnd) {
        duration = currentEnd.diff(currentStart);
      }

      const [update, fetchUpdate] = useUpdateDate({
        projectId,
        summaryId: summary.id,
        cell,
        duration,
        field: 'startDate',
      });

      const d: DateTime | null = cell.row.original.project.constructionStartDate || null;

      if (update.loading) return <CellLoading />;
      if (update.error) return <CellError />;

      return (
        <div
          className="flex h-full w-full items-start justify-start"
          data-testid={`cell-construction-start-date-${projectId}`}
        >
          <ReactDatePicker
            selected={d?.toJSDate() || null}
            onChange={(d: Date | null) => fetchUpdate(d ? DateTime.fromJSDate(d) : undefined)}
            closeOnScroll
            popperClassName="z-10!"
            customInput={
              <div
                className="h-full w-full grow cursor-pointer"
                data-testid={`cell-construction-start-date-input-${projectId}`}
              >
                {d?.toLocaleString() || <span className="opacity-50">Select date</span>}
              </div>
            }
          />
        </div>
      );
    },
  },
  {
    id: 'recommendation',
    exportLabel: 'Recommendation',
    header: () =>
      renderHeaderWithTooltip(
        'Recommendation',
        <div>Recommendation to accelerate or defer the project timeline based on schedule conflicts.</div>,
      ),
    accessorFn: (s) => {
      return getSummaryValue({
        summary: s,
        value: recommendationLabels[s.schedule.recommendation],
      });
    },
    cell: (cell) => {
      const s = cell.row.original;
      const projectId = s.project.id;
      const r = s.schedule.recommendation;
      const warning = useMemo(() => hasWarning(s.undigitizedAgencies), [s.undigitizedAgencies]);
      const cellTestId = `cell-recommendation-${projectId}`;

      if (isMissingDates(s)) {
        return <MissingInfo projectId={s.project.id} data-testid={cellTestId} />;
      }

      if (s.computeStatus === 'processing') {
        return (
          <span data-testid={cellTestId}>
            <CircularProgress indeterminate />
          </span>
        );
      }

      if (s.computeStatus === 'error') {
        if (!hasPriorComputeData(s)) {
          return <EmptyCell />;
        }
        const label = cell.getValue() as string | undefined;
        return <StaleCell value={label ?? '—'} data-testid={cellTestId} />;
      }

      if (warning) {
        return (
          <StatusBadge
            color="grey"
            aria-label={rowBadgeLabel('Recommendation', '-', s.project.name, 'Not available')}
            tooltip={{
              title: '⚠️ NOTE:',
              description:
                'Recommendation not available as this project contains non-digitized data for some agencies.',
            }}
            data-testid={cellTestId}
          >
            <span>-</span>
          </StatusBadge>
        );
      }

      let color: BadgeColor = 'yellow';
      const projectStart = s.project.constructionStartDate;
      const projectEnd = s.project.constructionEndDate;
      const start = s.schedule.conflictsStart;
      const end = s.schedule.conflictsEnd;
      let title = cell.getValue();

      if (r === ConflictRec.Accelerate) {
        if (start && projectStart && projectEnd) {
          const projectDuration = projectEnd.diff(projectStart);
          const rollback = start.minus(projectDuration);
          if (rollback) {
            title += ` to ${rollback.toLocaleString()}`;
          }
        }
      } else if (r === ConflictRec.Defer) {
        if (end) {
          title += ` to ${end?.toLocaleString()}`;
        }
      } else if (r === ConflictRec.NoScheduleConflict) {
        color = 'green';
      }

      const tooltip = {
        title: <span className="text-sm">{title}</span>,
        description: (
          <div className="text-sm">
            View conflict details{' '}
            <Link
              className="text-blue-50 underline hover:text-blue-60"
              to="/projects/$projectId/$tabId"
              params={{ projectId, tabId: 'intelligence' }}
            >
              here
            </Link>
            .
          </div>
        ),
      };

      return (
        <StatusBadge
          color={color}
          tooltip={tooltip}
          aria-label={rowBadgeLabel('Recommendation', cell.getValue() as string, s.project.name)}
          data-testid={cellTestId}
        >
          <span>{cell.getValue()}</span>
        </StatusBadge>
      );
    },
  },
  {
    id: 'projectManager',
    header: 'PM',
    accessorFn: (s) => s.project?.projectManager?.name || '',
    cell: (cell) => {
      const id = cell.row.original.project.id;
      const value = cell.getValue<string>();
      return <span data-testid={`cell-pm-${id}`}>{value}</span>;
    },
  },
  {
    id: 'notes',
    header: 'Notes',
    accessorKey: 'notes',
    minSize: 200,
    cell: (cell) => {
      const summaryId = cell.row.original.id;
      const projectId = cell.row.original.project.id;
      const initialValue = cell.getValue();
      const columnId = cell.column.id;

      async function onCommit(notes: string) {
        const res = await apiUpdateSummary(summaryId, { notes });
        if (!res.id) throw new Error('Bad response updating summary notes!');
        cell.table.options.meta?.updateValue?.(projectId, [
          {
            columnId,
            level: 'summary',
            value: notes,
          },
        ]);

        capturePosthogEvent(POSTHOG_EVENTS.conflictsSummaryNotesUpdated, {
          summary_id: summaryId,
          notes_length: notes.length,
        });
      }

      return (
        <div>
          <FreeFormTextCell initialValue={initialValue} onCommit={onCommit} data-testid={`cell-notes-${projectId}`} />
        </div>
      );
    },
  },
  {
    id: 'projectConflicts',
    header: 'Project Conflicts',
    columns: [
      {
        id: 'conflictArea',
        exportLabel: 'Conflict Area (sq. ft.)',
        header: () =>
          renderHeaderWithTooltip(
            'Conflict Area (sq. ft.)',
            <div>
              The aggregate area of all <span className="font-semibold">paving projects</span>,{' '}
              <span className="font-semibold">paving moratoria</span>, and{' '}
              <span className="font-semibold">Capital Improvement Projects</span> that overlap this project.
            </div>,
          ),
        accessorFn: (summary) =>
          getSummaryValue({
            summary,
            value: getConflictArea(summary),
          }),
        cell: (cell) => {
          const agencies = cell.row.original.undigitizedAgencies;
          const warning = useMemo(() => hasWarning(agencies), [agencies]);
          const projectId = cell.row.original.project.id;

          return (
            <Cell
              warning={warning}
              value={Math.round(cell.getValue()).toLocaleString()}
              summary={cell.row.original}
              data-testid={`cell-conflict-area-${projectId}`}
            />
          );
        },
      },
      {
        id: 'percentConflict',
        exportLabel: '% Conflict',
        header: () =>
          renderHeaderWithTooltip(
            '% Conflict',
            <div>
              The percent overlap between the aggregate <span className="font-semibold">conflict area</span> and the
              project's <span className="font-semibold">right of way area</span>.
            </div>,
          ),
        accessorFn: (s) => {
          const a = getConflictArea(s);
          let value = 0;

          if (a || a === 0) {
            const denom = s.rightOfWayArea;

            if (denom) {
              value = Math.round((a / denom) * 100);
            }
          }

          return getSummaryValue({ summary: s, value });
        },
        cell: (cell) => {
          const v = cell.getValue();
          const agencies = cell.row.original.undigitizedAgencies;
          const warning = useMemo(() => hasWarning(agencies), [agencies]);
          const projectId = cell.row.original.project.id;
          return (
            <Cell
              warning={warning}
              value={v ? `${v.toLocaleString()}%` : `<1%`}
              summary={cell.row.original}
              data-testid={`cell-percent-conflict-${projectId}`}
            />
          );
        },
      },
      {
        id: 'moratoriaInfo',
        exportLabel: 'Paving Moratoria Info',
        accessorFn: (s) =>
          Object.values(s.moratoriaInfo ?? {})
            .map(
              (info) =>
                `${info.ownerName ?? 'Unknown Agency'}: ${
                  info.startDate?.year.toString() ?? 'Unknown'
                } - ${info.endDate?.year.toString() ?? 'Unknown'}`,
            )
            ?.join('\n'),
        header: () =>
          renderHeaderWithTooltip(
            'Paving Moratoria Info',
            <div>The start and end years of paving moratoria for each agency.</div>,
          ),
        cell: ({ getValue, row }) => {
          const projectId = row.original.project.id;
          return (
            <span className="whitespace-pre-wrap" data-testid={`cell-moratoria-info-${projectId}`}>
              {renderInfoTextCell({ text: getValue() as string, summary: row.original })}
            </span>
          );
        },
      },
      {
        id: 'pavingProjectsInfo',
        exportLabel: 'Paving Projects Info',
        header: () =>
          renderHeaderWithTooltip(
            'Paving Projects Info',
            <div>The start and end years of paving projects for each agency.</div>,
          ),
        accessorFn: (s) =>
          Object.values(s.pavingProjectsInfo ?? {})
            .map(
              (info) =>
                `${info.ownerName ?? 'Unknown Agency'}: ${
                  info.startDate?.year.toString() ?? 'Unknown'
                } - ${info.endDate?.year.toString() ?? 'Unknown'}`,
            )
            ?.join('\n'),
        cell: ({ getValue, row }) => {
          const projectId = row.original.project.id;
          return (
            <span className="whitespace-pre-wrap" data-testid={`cell-paving-projects-info-${projectId}`}>
              {renderInfoTextCell({ text: getValue() as string, summary: row.original })}
            </span>
          );
        },
      },
      {
        id: 'cipProjectsInfo',
        exportLabel: 'Capital Improvement Projects Info',
        header: () =>
          renderHeaderWithTooltip(
            'Capital Improvement Projects Info',
            <div>The start and end years of capital improvement projects for each agency.</div>,
          ),
        accessorFn: (s) =>
          Object.values(s.cipProjectsInfo ?? {})
            .map(
              (info) =>
                `${info.ownerName ?? 'Unknown Agency'}: ${
                  info.startDate?.year.toString() ?? 'Unknown'
                } - ${info.endDate?.year.toString() ?? 'Unknown'}`,
            )
            ?.join('\n'),
        cell: ({ getValue, row }) => {
          const projectId = row.original.project.id;
          return (
            <span className="whitespace-pre-wrap" data-testid={`cell-cip-projects-info-${projectId}`}>
              {renderInfoTextCell({
                text: getValue() as string,
                summary: row.original,
                testId: 'cip-projects-info-cell',
              })}
            </span>
          );
        },
      },
    ],
  },
  {
    id: 'rightOfWayInformation',
    header: 'Right of Way Information',
    columns: [
      {
        id: 'jurisdictionCount',
        exportLabel: 'Jurisdiction Count',
        header: () =>
          renderHeaderWithTooltip(
            'Jurisdiction Count',
            <div>
              The number of intersecting cities, counties, and state department of transportation districts in this
              project area.
            </div>,
          ),
        accessorFn: (summary) =>
          getSummaryValue({
            summary,
            value: getJurisdictionCount(summary),
          }),
        cell: (cell) => {
          const value = cell.getValue()?.toLocaleString() ?? '';
          const cities = cell.row.original.incorporatedPlacesCount;
          const counties = cell.row.original.countyCount;
          const dotDistricts = cell.row.original.landUseTransportationDistrictBoundariesCount;
          const projectId = cell.row.original.project.id;

          const tooltip = (
            <div className="flex flex-col">
              <div>
                Cities: <span className="font-semibold">{cities}</span>
              </div>
              <div>
                Counties: <span className="font-semibold">{counties}</span>
              </div>
              <div>
                DOT Districts: <span className="font-semibold">{dotDistricts}</span>
              </div>
            </div>
          );

          return (
            <Cell
              value={value}
              summary={cell.row.original}
              tooltip={tooltip}
              data-testid={`cell-jurisdiction-count-${projectId}`}
            />
          );
        },
      },
      {
        id: 'crossingsCount',
        exportLabel: 'Crossings Count',
        header: 'Crossings Count',
        columns: [
          {
            id: 'landUseRailroadsCount',
            exportLabel: 'Rail',
            header: () =>
              renderHeaderWithTooltip('Rail', <div>The number of rail crossings in this project area.</div>),
            accessorFn: (summary) =>
              getSummaryValue({
                summary,
                value: summary.landUseRailroadsCount,
              }),
            cell: (cell) => {
              const projectId = cell.row.original.project.id;
              return (
                <Cell
                  value={cell.getValue()?.toLocaleString() ?? ''}
                  summary={cell.row.original}
                  data-testid={`cell-rail-count-${projectId}`}
                />
              );
            },
          },
          {
            id: 'transitLinesCount',
            exportLabel: 'Transit',
            header: () =>
              renderHeaderWithTooltip('Transit', <div>The number of transit crossings in this project area.</div>),
            accessorFn: (summary) => getSummaryValue({ summary, value: summary.transitLinesCount }),
            cell: (cell) => {
              const projectId = cell.row.original.project.id;
              return (
                <Cell
                  value={cell.getValue()?.toLocaleString() ?? ''}
                  summary={cell.row.original}
                  data-testid={`cell-transit-count-${projectId}`}
                />
              );
            },
          },
          {
            id: 'landUseRoadsBufferCount',
            exportLabel: 'Roads',
            header: () =>
              renderHeaderWithTooltip('Roads', <div>The number of road crossings in this project area.</div>),
            accessorFn: (summary) =>
              getSummaryValue({
                summary,
                value: summary.landUseRoadsBufferCount,
              }),
            cell: (cell) => {
              const projectId = cell.row.original.project.id;
              return (
                <Cell
                  value={cell.getValue()?.toLocaleString() ?? ''}
                  summary={cell.row.original}
                  data-testid={`cell-roads-count-${projectId}`}
                />
              );
            },
          },
        ],
      },
    ],
  },
];

export const constructionEndDateColumn: ExportableColumnDef<IntelligenceSummary> = {
  id: 'constructionEndDate',
  header: 'Construction End Date',
  accessorFn: (s) => s.project.constructionEndDate?.toLocaleString() || '',
  cell: (cell) => {
    const summary = cell.row.original;
    const projectId = summary.project.id;
    const currentStart = summary.project.constructionStartDate;

    const [update, fetchUpdate] = useUpdateDate({
      projectId,
      summaryId: summary.id,
      cell,
      currentStart,
      field: 'endDate',
    });

    const d: DateTime | null = summary.project.constructionEndDate || null;

    if (update.loading) return <CellLoading />;
    if (update.error) return <CellError />;

    return (
      <div
        className="flex h-full w-full items-start justify-start"
        data-testid={`cell-construction-end-date-${projectId}`}
      >
        <ReactDatePicker
          selected={d?.toJSDate() || null}
          onChange={(d: Date | null) => fetchUpdate(d ? DateTime.fromJSDate(d) : undefined)}
          closeOnScroll
          popperClassName="z-10!"
          customInput={
            <div
              className="h-full w-full grow cursor-pointer"
              data-testid={`cell-construction-end-date-input-${projectId}`}
            >
              {d?.toLocaleString() || <span className="opacity-50">Select date</span>}
            </div>
          }
        />
      </div>
    );
  },
};
